'use client'
import { useEffect, useState } from 'react'
import Script from 'next/script'
import { Context } from '@/types/app'
import { AdAllianceLib } from './AdAlliance.lib'

type AdAllianceProps = { context: Context }

function onIdle(cb: () => void, timeout = 1500) {
  const ric: any = (window as any).requestIdleCallback
  if (typeof ric === 'function') ric(cb, { timeout })
  else setTimeout(cb, timeout)
}

async function waitForTopReady(maxMs = 4000) {
  const t0 = performance.now()
  while (performance.now() - t0 < maxMs) {
    const mb: any = (window as any).MoltenBundle
    if (
      mb &&
      typeof mb.getClient === 'function' &&
      typeof mb.push === 'function'
    ) {
      return true
    }
    await new Promise((r) => setTimeout(r, 50))
  }
  return false
}

async function injectBottomOnce(BOTTOM: string) {
  if ((window as any).__ADA_BOTTOM_INJECTED__) return
  ;(window as any).__ADA_BOTTOM_INJECTED__ = true

  console.log('[AdA] injectBottomOnce:start')
  await waitForTopReady()
  console.log('[AdA] injectBottomOnce:topReady')

  if (document.getElementById('molten-bottom')) return
  const s = document.createElement('script')
  s.id = 'molten-bottom'
  s.src = BOTTOM
  s.async = true
  s.crossOrigin = 'anonymous'
  s.onload = () => {
    console.log('[AdA] molten-bottom onload')
    const mb: any = (window as any).MoltenBundle
    const client = mb?.getClient?.()

    setTimeout(() => {
      const positions = client?.getAdConfig?.()?.positions
      const noPositions =
        !positions || (Array.isArray(positions) && positions.length === 0)

      if (noPositions) {
        const nodes = Array.from(
          document.querySelectorAll('[data-ada-container]'),
        ) as HTMLElement[]
        const ids = nodes
          .map((el) => el.id || el.getAttribute('data-ada-container') || '')
          .filter(Boolean)
          .map((s) =>
            s.replace(/_(mobile|desktop|default)$/i, '').toLowerCase(),
          )

        const uniq = Array.from(new Set(ids))
        if (typeof (mb as any).pushArray === 'function') {
          ;(mb as any).pushArray(uniq)
          console.log('[AdA] pushArray (repair)', uniq)
        } else {
          uniq.forEach((x) => mb.push(x))
          console.log('[AdA] mb.push (repair) each', uniq)
        }
        ;(window as any).__ADA_ANY_SLOT_PUSHED__ = true
      }

      onIdle(() => {
        try {
          client?.reloadAds?.()
        } catch {
          console.warn('AdA: client.reloadAds() failed')
        }
        console.log('[AdA] client.reloadAds() idle')
      }, 1500)
    }, 800)
  }
  s.onerror = (e) => {
    ;(window as any).__ADA_BOTTOM_INJECTED__ = false
    console.error('[AdA] molten-bottom load error', e)
  }
  document.body.appendChild(s)
}

export const AdAlliance: React.FC<AdAllianceProps> = ({ context }) => {
  const [pageLoaded, setPageLoaded] = useState(false)

  const isPreview = context.adMode === 'preview'
  const TOP = isPreview
    ? 'https://adctrl.emsmobile.de/molten/preview/wetter.de/MoltenBundle.js'
    : '/mbundle/top.js'
  const BOTTOM = isPreview
    ? 'https://adctrl.emsmobile.de/molten/preview/wetter.de/MoltenBundle.bottom.js'
    : '/mbundle/bottom.js'

  useEffect(() => {
    setPageLoaded(true)
  }, [])

  useEffect(() => {
    AdAllianceLib.initialize(context)
  }, [context])

  return pageLoaded ? (
    <>
      <Script
        id="molten-top"
        src={TOP}
        strategy="afterInteractive"
        crossOrigin="anonymous"
        onLoad={() => {
          onIdle(() => {
            void injectBottomOnce(BOTTOM)
          }, 800)
        }}
        onError={(e) => console.error('[AdA] molten-top load error', e)}
      />
    </>
  ) : null
}
